import { HTTP_METHOD } from '../actions/constants/methods';
import axios from '../utils/axios';
import globals from '../globals/communityGlobals';
import { logoutAndRedirect } from '../actions/global/auth';
import { authorisationExpiredAction, createAPIErrorHandler } from './errorHandling';

const apiUrl = globals.urls.apiUrl;

const mapAxiosObject = (url, method, data, isCommand, auth, overrideApiUrl) => {
  const timestampPrefix = url.search('\\?') >= 0 ? '&' : '?';
  const isAuthObject = typeof auth === 'object' && auth !== null;
  const request = {
    baseURL: overrideApiUrl || apiUrl,
    method,
    url: isCommand ? url : `${url + timestampPrefix}tid=${isAuthObject ? auth.tid : '0'}&v=${Date.now().toString()}`,
    data: isCommand && !data ? {} : data,
    headers: {
      Authorization: `Bearer ${isAuthObject ? auth.jwtToken : auth}`,
    },
  };
  return axios(request);
};

const callAxiosParallel = (auth, models = [], overrideApiUrl) => {
  const requests = models.map(model => {
    const url = model.url;
    const method = model.method || HTTP_METHOD.GET;
    const requestData = model.data || null;
    const isCommand = method.toUpperCase() === HTTP_METHOD.POST;

    return mapAxiosObject(url, method, requestData, isCommand, auth, overrideApiUrl);
  });
  return Promise.all(requests);
};

function callAPIMiddleware({ dispatch, getState }) {
  return next => action => {
    if (action.error) {
      // Catches unexpected errors
      // eslint-disable-next-line no-console
      console.error(action.error);
    }
    const currentState = getState();
    const { types, buildCallParameters, shouldCallAPI = () => true, ...rest } = action;

    if (!types) {
      return next(action);
    }
    if (!Array.isArray(types) || types.length !== 3 || !types.every(type => typeof type === 'string')) {
      throw new Error('Expected an array of three string types.');
    }

    if (!currentState.global.auth.jwtToken) {
      return dispatch(logoutAndRedirect());
    }

    if (typeof buildCallParameters !== 'function') {
      throw new Error('Expected buildCallParameters to be a function.');
    }

    const [requestType, successType, failureType] = types;

    if (!shouldCallAPI(currentState)) {
      return false;
    }

    dispatch({
      type: requestType,
      ...rest,
    });

    const { method, path: url, data, overrideApiUrl } = buildCallParameters(currentState);
    const isCommand = method.toUpperCase() === HTTP_METHOD.POST;

    const apiErrorHandler = createAPIErrorHandler({
      unauthorized: () => dispatch(authorisationExpiredAction),
      other: error =>
        dispatch({
          status: error.response ? error.response.status : null,
          message: error.response ? error.response.data || error.response.statusText : error.message,
          type: failureType,
          ...rest,
        }),
    });

    return mapAxiosObject(url, method, data, isCommand, currentState.global.auth, overrideApiUrl)
      .then(response =>
        dispatch({
          response,
          receivedAt: Date.now(),
          type: successType,
          ...rest,
        }),
      )
      .catch(apiErrorHandler);
  };
}

function callAPIMiddlewarePromise(action, dispatch, getState) {
  if (action.error) {
    // Catches unexpected errors
    // eslint-disable-next-line no-console
    console.error(action.error);
  }
  const currentState = getState();
  const { types, buildCallParameters, shouldCallAPI = () => true, ...rest } = action;

  if (!currentState.global.auth.jwtToken) {
    return dispatch(logoutAndRedirect());
  }

  if (typeof buildCallParameters !== 'function') {
    throw new Error('Expected buildCallParameters to be a function.');
  }

  const [requestType] = types;

  if (!shouldCallAPI(currentState)) {
    return false;
  }

  dispatch({
    type: requestType,
    ...rest,
  });

  const { method, path: url, data, overrideApiUrl } = buildCallParameters(currentState);
  const isCommand = method.toUpperCase() === HTTP_METHOD.POST;

  return mapAxiosObject(url, method, data, isCommand, currentState.global.auth, overrideApiUrl);
}

function callAxios(auth, url, method, data, overrideApiUrl) {
  // eslint-disable-next-line no-param-reassign
  method = method || HTTP_METHOD.GET;
  const isCommand = method.toUpperCase() === HTTP_METHOD.POST;

  return mapAxiosObject(url, method, data, isCommand, auth, overrideApiUrl);
}

export { callAPIMiddleware, callAPIMiddlewarePromise, callAxios, callAxiosParallel };
